import { TrendingUp, FileText, Users, DollarSign, ArrowRight, AlertTriangle, Clock, ClipboardList, Calendar } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Card, CardContent } from '@/components/ui/Card';

interface TabGeralProps {
  eventoId: string;
  onNavigateTab: (tab: string) => void;
}

export function TabGeral({ eventoId, onNavigateTab }: TabGeralProps) {
  const { data: financeiro } = trpc.eventos.getFinanceiro.useQuery({ id: eventoId });
  const { data: resumoLivroCaixa } = trpc.livroCaixa.getResumoFinanceiro.useQuery({ eventoId });
  const { data: evento } = trpc.eventos.getById.useQuery({ id: eventoId });
  const { data: dashboard } = trpc.eventos.getEventoDashboard.useQuery({ id: eventoId });

  const formatCurrency = (value: number | string | null) => {
    if (value === null || value === undefined) return 'R$ 0,00';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  const formatDate = (date: string) => {
    const [y, m, d] = date.split('-');
    return `${d}/${m}/${y}`;
  };

  // Calculate progress based on inscriptions vs limit
  const totalInscricoes = financeiro?.inscricoesTotal || 0;
  const inscricoesPagas = financeiro?.inscricoesPagas || 0;
  const limite = evento?.limiteParticipantes || 0;
  const progressPercent = limite > 0 ? Math.round((totalInscricoes / limite) * 100) : 0;

  // Count inscriptions by status
  const inscricoesPendentes = financeiro?.inscricoesPendentes || 0;
  const inscricoesCanceladas = financeiro?.inscricoesCanceladas || 0;

  // Checklist alerts
  const alertasVencidos = dashboard?.checklistAlertas.filter(a => a.status === 'vencido') || [];
  const alertasUrgentes = dashboard?.checklistAlertas.filter(a => a.status === 'urgente') || [];
  const alertasProximos = dashboard?.checklistAlertas.filter(a => a.status === 'proximo') || [];
  const totalAlertas = (dashboard?.checklistAlertas.length) || 0;

  const getDiasTexto = (dataPrazo: string) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const prazo = new Date(dataPrazo + 'T00:00:00');
    const diff = Math.round((prazo.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return `${Math.abs(diff)} dia${Math.abs(diff) !== 1 ? 's' : ''} em atraso`;
    if (diff === 0) return 'Vence hoje';
    if (diff === 1) return 'Vence amanhã';
    return `Vence em ${diff} dias`;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Progresso */}
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Progresso</span>
              <TrendingUp className="h-4 w-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{progressPercent}%</p>
            <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all"
                style={{ width: `${Math.min(progressPercent, 100)}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {totalInscricoes} de {limite || '∞'} inscritos
            </p>
          </CardContent>
        </Card>

        {/* Inscrições */}
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Inscrições</span>
              <Users className="h-4 w-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{totalInscricoes}</p>
            <p className="text-xs text-gray-500 mt-1">
              {inscricoesPagas > 0 && `+${inscricoesPagas} pagas`}
            </p>
          </CardContent>
        </Card>

        {/* Patrocínios */}
        <Card className="border border-gray-200 cursor-pointer hover:border-gray-300 transition-colors" onClick={() => onNavigateTab('patrocinadores')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Patrocínios</span>
              <DollarSign className="h-4 w-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(dashboard?.patrocinadores.valorRecebido || 0)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {dashboard?.patrocinadores.pendentes
                ? `${dashboard.patrocinadores.pendentes} pendente${dashboard.patrocinadores.pendentes > 1 ? 's' : ''}`
                : 'de'}{' '}
              {formatCurrency(dashboard?.patrocinadores.valorTotal || 0)}
            </p>
          </CardContent>
        </Card>

        {/* Gastos */}
        <Card className="border border-gray-200 cursor-pointer hover:border-gray-300 transition-colors" onClick={() => onNavigateTab('financeiro')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Gastos</span>
              <FileText className="h-4 w-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(dashboard?.gastos.valorPago || 0)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {dashboard?.gastos.pendentes
                ? `${dashboard.gastos.pendentes} pendente${dashboard.gastos.pendentes > 1 ? 's' : ''}`
                : 'de'}{' '}
              {formatCurrency(dashboard?.gastos.valorTotal || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Resumo Financeiro + Prazos de Checklists lado a lado */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Resumo Financeiro */}
        <Card className="border border-gray-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">Resumo Financeiro</h3>
                <p className="text-sm text-gray-500">Visão geral das finanças</p>
              </div>
              <button
                onClick={() => onNavigateTab('financeiro')}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                Ver detalhes <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Receitas</span>
                <span className="font-semibold text-green-600">
                  {formatCurrency(resumoLivroCaixa?.totalReceitas || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Despesas</span>
                <span className="font-semibold text-red-600">
                  {formatCurrency(resumoLivroCaixa?.totalDespesas || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-900 font-medium">Saldo</span>
                <span className={`font-bold text-lg ${
                  (resumoLivroCaixa?.saldo || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatCurrency(resumoLivroCaixa?.saldo || 0)}
                </span>
              </div>

              {/* Detalhamento */}
              <div className="pt-2 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <button
                    onClick={() => onNavigateTab('patrocinadores')}
                    className="text-gray-500 hover:text-primary-600 flex items-center gap-1"
                  >
                    Patrocínios
                  </button>
                  <span className="text-gray-600">
                    {dashboard?.patrocinadores.recebidos || 0} recebido{(dashboard?.patrocinadores.recebidos || 0) !== 1 ? 's' : ''} / {dashboard?.patrocinadores.total || 0} total
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <button
                    onClick={() => onNavigateTab('financeiro')}
                    className="text-gray-500 hover:text-primary-600 flex items-center gap-1"
                  >
                    Gastos
                  </button>
                  <span className="text-gray-600">
                    {dashboard?.gastos.pagos || 0} pago{(dashboard?.gastos.pagos || 0) !== 1 ? 's' : ''} / {dashboard?.gastos.total || 0} total
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Prazos de Checklists */}
        <Card className="border border-gray-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-gray-700" />
                <div>
                  <h3 className="font-semibold text-gray-900">Prazos de Checklists</h3>
                  <p className="text-sm text-gray-500">
                    {totalAlertas > 0
                      ? `${totalAlertas} ${totalAlertas === 1 ? 'item requer atenção' : 'itens requerem atenção'}`
                      : 'Nenhum prazo pendente'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => onNavigateTab('comissoes')}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                Ver comissões <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {totalAlertas > 0 ? (
              <div className="space-y-4">
                {/* Vencidos */}
                {alertasVencidos.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <span className="text-sm font-semibold text-red-700">
                        Vencidos ({alertasVencidos.length})
                      </span>
                    </div>
                    <div className="space-y-1">
                      {alertasVencidos.map((alerta) => (
                        <div
                          key={alerta.itemId}
                          className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 text-sm">{alerta.itemNome}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-gray-500">{alerta.checklistNome}</span>
                              <span className="text-xs text-gray-300">&middot;</span>
                              <span className="text-xs text-gray-500">{alerta.comissaoNome}</span>
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="flex items-center gap-1 text-xs text-red-700">
                              <Calendar className="h-3 w-3" />
                              {formatDate(alerta.dataPrazo)}
                            </div>
                            <p className="text-xs font-medium text-red-600 mt-0.5">
                              {getDiasTexto(alerta.dataPrazo)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Urgentes */}
                {alertasUrgentes.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-700">
                        Urgentes ({alertasUrgentes.length})
                      </span>
                    </div>
                    <div className="space-y-1">
                      {alertasUrgentes.map((alerta) => (
                        <div
                          key={alerta.itemId}
                          className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 text-sm">{alerta.itemNome}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-gray-500">{alerta.checklistNome}</span>
                              <span className="text-xs text-gray-300">&middot;</span>
                              <span className="text-xs text-gray-500">{alerta.comissaoNome}</span>
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="flex items-center gap-1 text-xs text-amber-700">
                              <Calendar className="h-3 w-3" />
                              {formatDate(alerta.dataPrazo)}
                            </div>
                            <p className="text-xs font-medium text-amber-600 mt-0.5">
                              {getDiasTexto(alerta.dataPrazo)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Próximos */}
                {alertasProximos.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-semibold text-blue-700">
                        Próximos ({alertasProximos.length})
                      </span>
                    </div>
                    <div className="space-y-1">
                      {alertasProximos.map((alerta) => (
                        <div
                          key={alerta.itemId}
                          className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 text-sm">{alerta.itemNome}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-gray-500">{alerta.checklistNome}</span>
                              <span className="text-xs text-gray-300">&middot;</span>
                              <span className="text-xs text-gray-500">{alerta.comissaoNome}</span>
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="flex items-center gap-1 text-xs text-blue-700">
                              <Calendar className="h-3 w-3" />
                              {formatDate(alerta.dataPrazo)}
                            </div>
                            <p className="text-xs font-medium text-blue-600 mt-0.5">
                              {getDiasTexto(alerta.dataPrazo)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8 text-gray-400">
                <p>Todos os prazos em dia</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Resumo do Evento */}
      <Card className="border border-gray-200">
        <CardContent className="p-5">
          <h3 className="font-semibold text-gray-900 mb-1">Resumo do Evento</h3>
          <p className="text-sm text-gray-500 mb-6">Visão geral das principais métricas</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl font-bold text-gray-900">{totalInscricoes}</p>
              <p className="text-sm text-gray-500">Inscrições</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-green-600">{inscricoesPagas}</p>
              <p className="text-sm text-gray-500">Pagas</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-600">{inscricoesPendentes}</p>
              <p className="text-sm text-gray-500">Pendentes</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-orange-500">{inscricoesCanceladas}</p>
              <p className="text-sm text-gray-500">Canceladas</p>
            </div>
          </div>

          {/* Métricas adicionais */}
          <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-2 md:grid-cols-3 gap-6 text-center">
            <div>
              <p className="text-3xl font-bold text-gray-900">{evento?._count?.comissoes || 0}</p>
              <p className="text-sm text-gray-500">Comissões</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">{evento?._count?.checklists || 0}</p>
              <p className="text-sm text-gray-500">Checklists</p>
            </div>
            <div>
              <p className={`text-3xl font-bold ${totalAlertas > 0 ? 'text-amber-500' : 'text-gray-900'}`}>
                {totalAlertas}
              </p>
              <p className="text-sm text-gray-500">
                <ClipboardList className="inline h-3.5 w-3.5 mr-1" />
                Alertas de prazo
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
